import { z } from 'zod';

export const ProviderSchema = z.enum(['gmail', 'outlook', 'imap']);
export type Provider = z.infer<typeof ProviderSchema>;

export const UuidSchema = z.string().uuid();

/**
 * Provider message identifier — Gmail messageId, Graph internal id, RFC 5322
 * Message-ID, or the IMAP account-scoped `${accountId}:${rfcMessageId}` that
 * `imapCanonicalEmailId` builds. Used by `replyToMessageId`, `threadId`, etc.
 * The id space is deliberately a union, not one form: `replyToMessageId`
 * identifies a stored row, so live compose passes the row's `email_id` (scoped
 * on IMAP) while the approvals path passes a bare RFC id. An adapter splicing
 * this into a wire header owns the reduction to the form that header requires.
 *
 * Because adapters splice this value into outbound RFC 5322 headers (e.g.
 * `In-Reply-To`, `References`), reject ASCII control characters and Unicode
 * line separators at the contract layer so a header-injection vector cannot
 * land via any callsite. Same disallowed-set as `EmailDisplayNameSchema`,
 * loosened to allow `"` and `\` (some provider ids contain them).
 */
// deno-lint-ignore no-control-regex
// eslint-disable-next-line no-control-regex
const DISALLOWED_HEADER_VALUE_CHARS = /[\x00-\x1F\x7F\u2028\u2029\uFEFF]/;

export const ProviderMessageIdSchema = z
  .string()
  .min(1)
  .max(1024)
  .refine((value) => !DISALLOWED_HEADER_VALUE_CHARS.test(value), {
    message: 'message id must not contain control or line-separator characters',
  });

export const LabelRefSchema = z.string().min(1).max(256);

/**
 * Email address. zod's built-in `.email()` accepts CR/LF in the local part —
 * a header-injection vector for any adapter that builds raw MIME headers.
 * Reject control characters at the contract layer so adapters never see them.
 */
export const MailAddressSchema = z
  .string()
  .email()
  .max(320)
  .refine((value) => !/[\r\n\t]/.test(value), {
    message: 'mail address must not contain CR, LF, or TAB',
  });

const BODY_SIZE_LIMIT = 10_485_760;
const ATTACHMENT_SIZE_LIMIT = 25 * 1024 * 1024;

/**
 * Total request payload budget (bodies + attachments). Picked to accommodate
 * Gmail's 25 MB send cap and Microsoft Graph's 4 MB inline / 150 MB session
 * limits. The `superRefine` on `SendEmailSchema` enforces this against the
 * sum of bodyHtml + bodyText + attachments[].size.
 */
export const TOTAL_SEND_PAYLOAD_BUDGET = 30 * 1024 * 1024;

export const BodyHtmlSchema = z.string().max(BODY_SIZE_LIMIT);
export const BodyTextSchema = z.string().max(BODY_SIZE_LIMIT);
export const SubjectSchema = z.string().max(998);

/**
 * Display name for the `From:` header of an outbound email (RFC 5322 §3.4.1
 * `display-name`). Rejects:
 * - ASCII control chars (0x00–0x1F, 0x7F) — CRLF header-injection primitive.
 * - RFC 5322 `quoted-string` terminators `"` and `\` — quoted-string escape
 *   injection (`Foo" <attacker@evil.com>, "Bar`).
 * - Unicode line separators U+2028, U+2029, and BOM U+FEFF — some header
 *   parsers and display-name renderers fold these as line terminators.
 *
 * Whitespace-only input is coerced to `undefined` so `requestedFromName ??
 * dbFallback` in the validator correctly falls through. Without this coercion,
 * a client-sent `''` defeats the DB fallback via nullish-coalescing.
 *
 * The 256-byte cap is measured against the UTF-8 encoded length (not JS
 * code units) to bound the header size under multi-byte input.
 */
// deno-lint-ignore no-control-regex
// eslint-disable-next-line no-control-regex
const DISALLOWED_DISPLAY_NAME_CHARS = /[\x00-\x1F\x7F"\\\u2028\u2029\uFEFF]/;

// Hoisted so each validation doesn't allocate a new TextEncoder. Every
// outbound send runs this refine — module-level reuse matches the rest of
// the codebase. Note: `TextEncoder.encode()` replaces unpaired UTF-16
// surrogates with U+FFFD (3 UTF-8 bytes each), which inflates malformed
// input and keeps the byte-cap fail-closed rather than crashing.
const DISPLAY_NAME_ENCODER = new TextEncoder();

export const EmailDisplayNameSchema = z.preprocess(
  (value) => {
    if (typeof value !== 'string') {
      return value;
    }
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
  },
  z
    .string()
    .refine((value) => !DISALLOWED_DISPLAY_NAME_CHARS.test(value), {
      message: 'display name contains disallowed characters',
    })
    .refine((value) => DISPLAY_NAME_ENCODER.encode(value).byteLength <= 256, {
      message: 'display name exceeds 256 bytes',
    })
    .optional(),
);

/**
 * MIME content-type. RFC 6838 type/subtype shape. CR/LF in `contentType`
 * is the classic MIME-header injection vector — reject at contract.
 *
 * Two properties keep this linear on hostile input (AGENTS.md → ReDoS
 * prevention). The parameter-value class excludes `;`, so a `;a=b` chain has
 * exactly one possible split — with `;` inside the value class the chain
 * re-splits every way on a match that fails at the end, which is exponential.
 * And the length cap must be a real gate: a plain `.max().regex()` chain does
 * not gate anything, because zod evaluates every string check and reports all
 * their issues, so the regex still runs on the oversized input. `.pipe()`
 * short-circuits — the second schema is never reached once the first fails.
 */
const ContentTypeSchema = z
  .string()
  .min(1)
  .max(128)
  .pipe(
    z
      .string()
      .regex(
        /^[a-zA-Z0-9!#$&^_+.-]+\/[a-zA-Z0-9!#$&^_+.-]+(?:;\s*[a-zA-Z0-9!#$&^_+.-]+=[^;\r\n]+)*$/,
        { message: 'contentType must be a valid MIME type/subtype' },
      ),
  );

export const AttachmentSchema = z
  .object({
    filename: z
      .string()
      .min(1)
      .max(255)
      .refine((value) => !/[\r\n\t/\\]/.test(value), {
        message: 'filename must not contain CR, LF, TAB, or path separators',
      }),
    contentType: ContentTypeSchema,
    contentBase64: z.string().max(Math.ceil((ATTACHMENT_SIZE_LIMIT * 4) / 3) + 4),
    size: z.number().int().min(0).max(ATTACHMENT_SIZE_LIMIT),
    /**
     * RFC 2392 Content-ID (without surrounding angle brackets). When present,
     * the adapter routes this attachment as an inline-image MIME part:
     *   - Gmail: emits `Content-ID: <id>` on a `multipart/related` part so the
     *     `<img src="cid:id">` references in `bodyHtml` resolve.
     *   - Outlook: sets `isInline: true` + `contentId` on the Graph
     *     `fileAttachment` so the same `cid:` references resolve recipient-side.
     * CR/LF/TAB rejected — header-injection vector identical to filename.
     */
    contentId: z
      .string()
      .min(1)
      .max(255)
      .refine((value) => !/[\r\n\t<>]/.test(value), {
        message: 'contentId must not contain CR, LF, TAB, or angle brackets',
      })
      .optional(),
  })
  .superRefine((attachment, ctx) => {
    // Padded standard base64 of n bytes is EXACTLY 4*ceil(n/3) chars, with
    // (3 - n%3) % 3 trailing `=`. The Outlook upload-session chunker derives
    // byte ranges from `size`, so ANY drift between the declared size and the
    // real payload would silently truncate or pad the uploaded bytes — require
    // the exact shape, not a tolerance.
    const expectedBase64Length = Math.ceil(attachment.size / 3) * 4;
    if (attachment.contentBase64.length !== expectedBase64Length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'contentBase64 length does not match declared size',
        path: ['size'],
      });
      return;
    }
    // Character-class scan — linear, no backtracking (ReDoS-safe).
    if (!/^[A-Za-z0-9+/]*={0,2}$/.test(attachment.contentBase64)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'contentBase64 must be padded standard base64',
        path: ['contentBase64'],
      });
      return;
    }
    // Trailing '=' count. The alphabet check above already bounds padding to at
    // most two trailing characters, so two endsWith probes are exhaustive (and,
    // unlike an index loop, have no unreachable branch for all-padding strings).
    let padding = 0;
    if (attachment.contentBase64.endsWith('==')) {
      padding = 2;
    } else if (attachment.contentBase64.endsWith('=')) {
      padding = 1;
    }
    if (padding !== (3 - (attachment.size % 3)) % 3) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'contentBase64 padding does not match declared size',
        path: ['size'],
      });
    }
  });

export const BaseRequestSchema = z.object({
  organizationId: UuidSchema.optional(),
  accountId: UuidSchema.optional(),
  idempotencyKey: z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9_-]{1,128}$/, {
      message: 'idempotencyKey must be 1–128 chars of [A-Za-z0-9_-]',
    })
    .optional(),
});
